import { useTranslation } from 'react-i18next';
import type { TransportSaveFailure } from '~/types/transportSave';
import { WarningBanner } from './Banner';
import { RetryBanner } from './RetryBanner';

interface TransportSaveFailedBannerProps {
  failure: TransportSaveFailure;
  onRetry: () => void;
  /** Worked out from the cart's own lines, so it reflects capacities added since the refusal. */
  productsNeedingCapacity: string[];
  isSaving: boolean;
  className?: string;
}

export function TransportSaveFailedBanner({
  failure,
  onRetry,
  productsNeedingCapacity,
  isSaving,
  className = 'mt-1.5',
}: Readonly<TransportSaveFailedBannerProps>) {
  const { t } = useTranslation(['cart', 'general']);

  const namesMissingCapacity =
    failure.kind === 'missing-capacity' && productsNeedingCapacity.length > 0;
  if (isSaving && !namesMissingCapacity) return null;

  if (failure.kind === 'none') {
    if (productsNeedingCapacity.length === 0) return null;
    return (
      <WarningBanner
        className={className}
        testId="vehicle-capacity-needed-banner"
      >
        {t('cart.vehicle_capacity_needed', {
          products: productsNeedingCapacity.join(', '),
        })}
      </WarningBanner>
    );
  }

  if (namesMissingCapacity) {
    return (
      <WarningBanner
        className={className}
        testId="transport-save-failed-banner"
      >
        {t('cart.transport_vehicle_capacity_missing', {
          products: productsNeedingCapacity.join(', '),
        })}
      </WarningBanner>
    );
  }

  return (
    <div className={className} data-testid="transport-save-failed-banner">
      <RetryBanner
        message={t('cart.transport_vehicle_save_failed')}
        retryLabel={t('general:general.retry')}
        onRetry={onRetry}
        testId="transport-save"
      />
    </div>
  );
}
